import type { ExecQualityRow } from "@/api/types";
import { EntityChip } from "@/components/ui";
import { Panel } from "@/components/ui";
import { formatDate, formatPct } from "@/lib/formatters";

interface ExecutionQualityTableProps {
  data: ExecQualityRow[];
  loading?: boolean;
}

function fmtMarkout(v: number | null | undefined): string {
  if (v == null) return "—";
  return `$${v.toFixed(4)}`;
}

const COLS = [
  {
    key: "entity_name",
    label: "ENTITY",
    flex: 2,
    minWidth: 140,
    tip: "Address or labelled entity",
  },
  {
    key: "revert_rate",
    label: "REVERT RATE",
    flex: 1,
    minWidth: 90,
    tip: "Fraction of this entity's transactions that reverted (failed). High revert rate = aggressive bot strategy or bad routing. Highlighted red above 10%.",
  },
  {
    key: "avg_markout_usd_1s",
    label: "MARKOUT 1s",
    flex: 1,
    minWidth: 90,
    tip: "Average profit/loss per swap measured 1 second after execution (USD). Positive = trade captured value before the market corrected.",
  },
  {
    key: "avg_markout_usd_300s",
    label: "MARKOUT 300s",
    flex: 1,
    minWidth: 90,
    tip: "Average profit/loss per swap measured 5 minutes (300 s) after execution (USD). Negative values indicate informed order flow (market moved against the LP).",
  },
  {
    key: "date",
    label: "DATE",
    flex: 1,
    minWidth: 90,
    tip: "Date of the most recent activity for this entity in the selected range",
  },
];

const cellBase = {
  padding: "0 8px",
  fontSize: 12,
  flex: 1,
  minWidth: 90,
  overflow: "hidden",
} as const;

export function ExecutionQualityTable({ data, loading }: ExecutionQualityTableProps) {
  return (
    <Panel
      title="EXECUTION QUALITY"
      description="Per-entity revert rates and markout P&L. A high revert rate or consistently negative 300 s markout signals an entity trading on stale prices or getting front-run. Hover column headers for definitions."
    >
      <div style={{ overflowX: "auto" }}>
        {/* header */}
        <div
          style={{
            display: "flex",
            width: "100%",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          {COLS.map((col) => (
            <div
              key={col.key}
              title={col.tip}
              style={{
                flex: col.flex,
                minWidth: col.minWidth,
                padding: "6px 8px",
                fontSize: 10,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--color-text-muted)",
                cursor: "help",
              }}
            >
              {col.label}
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 16 }}>
            {Array.from({ length: 5 }, (_, i) => (
              <div
                key={i}
                className="skeleton"
                style={{ height: 28, marginBottom: 4, borderRadius: 3 }}
              />
            ))}
          </div>
        ) : data.length === 0 ? (
          <div
            style={{
              padding: "32px 0",
              textAlign: "center",
              color: "var(--color-text-muted)",
              fontSize: 12,
            }}
          >
            No data for this range
          </div>
        ) : (
          data.slice(0, 50).map((row, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                borderBottom: "1px solid var(--color-border)",
                height: 36,
              }}
            >
              <div style={{ flex: 2, minWidth: 140, padding: "0 8px", overflow: "hidden" }}>
                <EntityChip name={row.entity_name} />
              </div>
              <div
                className="tabular"
                style={{
                  ...cellBase,
                  color: row.revert_rate > 0.1 ? "var(--color-red)" : "var(--color-text-primary)",
                }}
              >
                {formatPct(row.revert_rate)}
              </div>
              <div className="tabular" style={cellBase}>
                {fmtMarkout(row.avg_markout_usd_1s)}
              </div>
              <div className="tabular" style={cellBase}>
                {fmtMarkout(row.avg_markout_usd_300s)}
              </div>
              <div style={{ ...cellBase, color: "var(--color-text-muted)" }}>
                {formatDate(row.date)}
              </div>
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}
